import { useState, MutableRefObject, useEffect } from 'react';
import { ActionModalContent } from 'components/ProfileUIComponents';
import {
  faChevronRight,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm, Controller } from 'react-hook-form';
import { IOrgDetails } from 'typings/organization';
import { Text } from '@morressier/ts';
import { useRouter } from 'next/router';
import { getQuery } from 'utils/query';
import { emailRegex, FEATURE_FLAGS } from 'utils/constants';
import { VIEWS } from '../EmailAffiliation';
import { getOrganizations, getOrganizationsByDomain } from '../../hooks';
import { Container, ShortInput, Loader, ModalForm } from '../style';
import { LinkButton } from 'containers/Profile/ProfileFormModal/style';
import { logEvent } from 'utils/eventTracking';
import { useFeatureFlagContext } from 'context/FeatureFlagProvider';

interface IEmailProps {
  onGetOrgs: (orgs: IOrgDetails[], email: string) => void;
  handleNavigaiton: (nextView: VIEWS) => void;
  onDismiss: () => void;
  navigateToOrgSearchView: (email: string) => void;
  defaultEmail: string;
  isFormDirty: MutableRefObject<boolean>;
}
interface FormValues {
  email: string;
}

const EmailView = (props: IEmailProps) => {
  const router = useRouter();
  const queryParams = getQuery(router);
  const features = useFeatureFlagContext();

  const [isLoading, setIsLoading] = useState(false);
  const {
    onGetOrgs,
    handleNavigaiton,
    onDismiss,
    navigateToOrgSearchView,
    defaultEmail,
    isFormDirty
  } = props;

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors, isDirty }
  } = useForm<FormValues>({
    defaultValues: {
      email: defaultEmail || queryParams.email?.toString() || ''
    }
  });

  useEffect(() => {
    isFormDirty.current = isDirty || queryParams.flow === 'account-activation';
  }, [isDirty]);

  const onSubmit = async (data: FormValues) => {
    logEvent('PROFILE_ADD_AFFILIATION_EMAIL_INPUT_CONTINUE_CLICKED');
    const { email } = data;
    try {
      setIsLoading(true);

      const isSearchByDomainV2Enabled =
        features[FEATURE_FLAGS.SEARCH_ORG_BY_DOMAIN_V2];

      let data;
      if (isSearchByDomainV2Enabled) {
        const domain = email?.split('@')[1];
        data = await getOrganizationsByDomain(domain);
      } else {
        data = await getOrganizations(email);
      }

      if (data?.message === 'Success' && data?.organizations.length) {
        const orgs: IOrgDetails[] = data?.organizations.map(org => {
          return {
            ringgoldId: org.ringgoldId,
            departmentName: org.name,
            organizationName: org.topLevelOrganizationName,
            organizationType: org.organizationType,
            location: org.formattedLocation
          };
        });

        if (orgs.length)
          logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_MATCH_FOUND', {
            match_type: 'email'
          });
        onGetOrgs(orgs, email);
        handleNavigaiton(VIEWS.OrganizationView);
      } else if (data.message === 'Only professional emails are supported') {
        setError('email', {
          type: 'manual',
          message: 'We cannot verify affiliations without a professional email'
        });
      } else if (
        data.message === 'No match found' ||
        !data?.organizations.length
      ) {
        logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_NO_MATCH_FOUND', {
          match_type: 'email'
        });
        navigateToOrgSearchView(email);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(true);
    }
  };

  const onSearchOrg = (e: React.MouseEvent<HTMLButtonElement>) => {
    logEvent(
      'PROFILE_ADD_AFFILIATION_EMAIL_INPUT_NO_EMAIL_ADDRESS_OPTION_CLICKED'
    );
    e.preventDefault();
    navigateToOrgSearchView('');
  };

  let oldValue = '';

  return (
    <ModalForm onSubmit={handleSubmit(onSubmit)}>
      <ActionModalContent
        title="Add affiliation"
        primaryAction={{
          title: 'Continue',
          iconAfter: isLoading ? (
            <Loader icon={faSpinnerThird} />
          ) : (
            <FontAwesomeIcon icon={faChevronRight} />
          ),
          disabled: isLoading,
          type: 'submit'
        }}
        cautionaryAction={{
          title: 'Exit without saving',
          onClick: () => {
            logEvent('PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CLICKED', {
              step: 'EMAIL_INPUT'
            });
            onDismiss();
          },
          type: 'button'
        }}
      >
        <Container>
          <Text size="h3_new" fontWeight="bold">
            Add your email
          </Text>
          <Text>
            {queryParams.flow === 'account-activation'
              ? 'Verifying affiliation with an organization by adding your associated email address will let profile visitors know that they can trust the integrity of your professional experience. Affiliations may be added without a matching email address, but the system will not be able to verify them.'
              : 'Add trust and integrity to your Morressier profile using your professional email address.'}
          </Text>
        </Container>
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Professional Email is Required',
            pattern: {
              value: emailRegex,
              message: 'We cannot verify affiliations without a valid email'
            }
          }}
          render={({ field }) => (
            <ShortInput
              placeholder="e.g. fabius@morressier.com"
              {...field}
              error={errors.email?.message}
              onPointerLeave={() => {
                oldValue = field.value;
              }}
            />
          )}
        />
        <LinkButton btnType="add" onClick={onSearchOrg}>
          I don't have an email address linked to this affiliation
        </LinkButton>
      </ActionModalContent>
    </ModalForm>
  );
};
export default EmailView;
